import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CreateSubscriptionPayload,
  CreateSubscriptionRatePayload,
  CreateSubscriptionSubscriberPayload,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  PaginationQuery,
  PaginationResponse,
  Response,
  SortByQuery,
  Subscription,
  SubscriptionRate,
  SubscriptionSubscriber,
  UpdateSubscriptionPayload,
  UpdateSubscriptionRatePayload,
} from "~/types/api";

export function createSubscriptionApi({
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches a paginated list of subscriptions. */
  const getSubscriptions = (
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    const params: Record<string, unknown> = {};

    if (pagination) {
      params.page = pagination.number;
      params.per_page = pagination.size;
    }

    if (sortBy) {
      const sortByFields = Array.isArray(sortBy) ? sortBy : [sortBy];
      let sortByQueryValue = "";

      for (const sort of sortByFields) {
        if (sortByQueryValue) {
          sortByQueryValue += ",";
        }
        sortByQueryValue += `${sort.column}:${sort.direction}`;
      }

      if (sortByQueryValue) {
        params.sort = sortByQueryValue;
      }
    }

    if (includes) {
      const includeFields = Array.isArray(includes) ? includes : [includes];
      params.include = includeFields.join(",");
    }

    if (filterBy) {
      filterBy.forEach((filter) => {
        if (filter.value !== undefined && filter.value !== null) {
          params[`filter[${filter.column}]`] = filter.value;
        }
      });
    }

    return getRequest<PaginationResponse<Subscription>>("/subscriptions", { params });
  };

  /** Fetches a single subscription by ID. */
  const getSubscription = (subscriptionId: string, includes?: IncludeQuery) => {
    return getRequest<Response<Subscription>>(`/subscriptions/${subscriptionId}`, { params: includes });
  };

  /** Creates a new subscription. */
  const createSubscription = (data: CreateSubscriptionPayload) =>
    postRequest<Response<Subscription>>(`/subscriptions`, data);

  /** Updates an existing subscription. */
  const updateSubscription = (subscriptionId: string, data: UpdateSubscriptionPayload) =>
    putRequest<Response<Subscription>>(`/subscriptions/${subscriptionId}`, data);

  /** Deletes a subscription. */
  const deleteSubscription = (subscriptionId: string) =>
    deleteRequest<EmptyResponse>(`/subscriptions/${subscriptionId}`);

  /** Fetches a paginated list of rates for a subscription. */
  const getSubscriptionRates = (
    subscriptionId: string,
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    return getPaginationRequest<SubscriptionRate>(
      `/subscriptions/${subscriptionId}/rates`,
      pagination,
      sortBy,
      includes,
      filterBy,
    );
  };

  /** Fetches a single rate for a subscription. */
  const getSubscriptionRate = (subscriptionId: string, rateId: string, includes?: IncludeQuery) => {
    return getRequest<Response<SubscriptionRate>>(`/subscriptions/${subscriptionId}/rates/${rateId}`, {
      params: includes,
    });
  };

  /** Creates a new rate for a subscription. */
  const createSubscriptionRate = (subscriptionId: string, data: CreateSubscriptionRatePayload) =>
    postRequest<Response<SubscriptionRate>>(`/subscriptions/${subscriptionId}/rates`, data);

  /** Updates an existing rate for a subscription. */
  const updateSubscriptionRate = (subscriptionId: string, rateId: string, data: UpdateSubscriptionRatePayload) =>
    putRequest<Response<SubscriptionRate>>(`/subscriptions/${subscriptionId}/rates/${rateId}`, data);

  /** Deletes a rate from a subscription. */
  const deleteSubscriptionRate = (subscriptionId: string, rateId: string) =>
    deleteRequest<EmptyResponse>(`/subscriptions/${subscriptionId}/rates/${rateId}`);

  /** Unsubscribes a subscriber from a subscription. */
  const unsubscribeFromSubscription = (subscriptionId: string, subscriberId: string) =>
    postRequest<Response<SubscriptionSubscriber>>(
      `/subscriptions/${subscriptionId}/subscribers/${subscriberId}/unsubscribe`,
    );

  /** Adds a new subscriber to a subscription. */
  const createSubscriptionSubscriber = (subscriptionId: string, data: CreateSubscriptionSubscriberPayload) =>
    postRequest<Response<SubscriptionSubscriber>>(`/subscriptions/${subscriptionId}/subscribers`, data);

  /** Fetches a paginated list of subscribers for a subscription. */
  const getSubscriptionSubscribers = (
    subscriptionId: string,
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    const params: Record<string, unknown> = {};

    if (pagination) {
      params.page = pagination.number;
      params.per_page = pagination.size;
    }

    if (sortBy) {
      const sortByFields = Array.isArray(sortBy) ? sortBy : [sortBy];
      let sortByQueryValue = "";

      for (const sort of sortByFields) {
        if (sortByQueryValue) {
          sortByQueryValue += ",";
        }
        sortByQueryValue += `${sort.column}:${sort.direction}`;
      }

      if (sortByQueryValue) {
        params.sort = sortByQueryValue;
      }
    }

    if (includes) {
      const includeFields = Array.isArray(includes) ? includes : [includes];
      params.include = includeFields.join(",");
    }

    if (filterBy) {
      filterBy.forEach((filter) => {
        if (filter.value !== undefined && filter.value !== null) {
          params[`filter[${filter.column}]`] = filter.value;
        }
      });
    }

    return getRequest<PaginationResponse<SubscriptionSubscriber>>(`/subscriptions/${subscriptionId}/subscribers`, {
      params,
    });
  };

  return {
    getSubscriptions,
    getSubscription,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    getSubscriptionRates,
    getSubscriptionRate,
    createSubscriptionRate,
    updateSubscriptionRate,
    deleteSubscriptionRate,
    unsubscribeFromSubscription,
    createSubscriptionSubscriber,
    getSubscriptionSubscribers,
  };
}

export type SubscriptionApi = ReturnType<typeof createSubscriptionApi>;
